import React, {
	useCallback, useEffect, useRef, useState, useMemo,
} from 'react';
import {useSelector} from 'react-redux';
import ReactWaves from '@dschoon/react-waves';
import {getTimeString} from '../../utils/date-utils';
import {usePlayer} from './context/playerContext';
import PlayBtn from '../../components/PlayBtn';
import WavePlaceholder from '../../components/WavePlaceholder';

const options = {
	barHeight: 1,
	barWidth: 6,
	barGap: 1,
	cursorWidth: 0,
	height: 40,
	hideScrollbar: true,
	progressColor: '#10DB92',
	responsive: true,
	waveColor: '#C7C7C7',
};

const AudioPlayer = ({track, isVisible}) => {
	const wavesurferRef = useRef(null);
	const [isActive, setIsActive] = useState(false);
	const waveContainerRef = useRef(null);
	const buttonRef = useRef(null);
	const [localProgress, setLocalProgress] = useState(0);
	const [loadedTracks, setLoadedTracks] = useState(() => {
		return {};
	});
	const wavesurferInstances = useRef({});

	const {currentTrack} = useSelector((state) => state.player);
	const {
		playing,
		progress,
		togglePlayPause,
		loadTrack,
		preparePeaks,
		setProgress,
		setProgress2,
		barHeight,
		barWidth,
		barGap,
		play,
		setPlay,
	} = usePlayer();

	const toggleTrackHandler = useCallback(() => {
		if (track.src && !isActive) {
			loadTrack(track);
			setPlay(true);
		}
	}, [isActive, loadTrack, setPlay, track]);

	useEffect(() => {
		setIsActive(currentTrack.id === track.id);
		if (currentTrack.id !== track.id) {
			setLocalProgress(0);
		}
	}, [track, currentTrack]);

	useEffect(() => {
		if (isActive) {
			setLocalProgress(progress);
		}
	}, [isActive, progress]);

	const onReady = useCallback(({ wavesurfer }) => {
		wavesurferInstances.current[track.id] = wavesurfer;
		wavesurfer.setVolume(0);
		const time = localProgress / 100;
		if (Number.isFinite(time)) {
			wavesurfer.seekTo(Math.min(Math.max(time, 0), 1));
		}

		setLoadedTracks(prev => ({
			...prev,
			[track.id]: true
		}));
	}, [localProgress, track.id]);

	useEffect(() => {
		if (isActive && loadedTracks[track.id] && wavesurferInstances.current[track.id]) {
			const time = localProgress / 100;
			if (Number.isFinite(time)) {
				wavesurferInstances.current[track.id].seekTo(Math.min(Math.max(time, 0), 1));
			}
		}
	}, [localProgress, isActive, track.id, loadedTracks]);

	const handleWaveClick = useCallback((e) => {
		e.preventDefault();
		e.stopPropagation();
		
		const container = waveContainerRef.current;
		if (!container) return;
		
		const rect = container.getBoundingClientRect();
		const width = rect.width;

		if (!Number.isFinite(width) || width <= 0) {
			return;
		}

		const clickX = e.clientX - rect.left;
		const isBeginningClick = clickX <= width * 0.1;
		
		let targetProgress;
		if (isBeginningClick) {
			targetProgress = 0;
		} else {
			targetProgress = Math.min(Math.max((clickX / width) * 100, 0), 100);
		}

		setLocalProgress(targetProgress);

		if (isActive) {
			setProgress(targetProgress);
			setProgress2(targetProgress);

			if (wavesurferInstances.current[track.id]) {
				const time = targetProgress / 100;
				if (Number.isFinite(time)) {
					wavesurferInstances.current[track.id].seekTo(Math.min(Math.max(time, 0), 1));
				}
			}
		} else {
			loadTrack(track);
			setProgress(targetProgress);
			setProgress2(targetProgress);
			setPlay(true);
		}
	}, [isActive, setProgress, setProgress2, loadTrack, setPlay, track]);

	useEffect(() => {
		const container = waveContainerRef.current;
		if (container) {
			container.addEventListener('click', handleWaveClick);
			return () => {
				container.removeEventListener('click', handleWaveClick);
			};
		}
	}, [handleWaveClick]);

	useEffect(() => {
		return () => {
			if (wavesurferInstances.current[track.id]) {
				wavesurferInstances.current[track.id].destroy();
				delete wavesurferInstances.current[track.id];
			}
		};
	}, [track.id]);

	useEffect(() => {
		if (buttonRef.current) {
			const buttonElement = buttonRef.current.querySelector('button');
			if (buttonElement) {
				const isPaused = !(playing && isActive && play);
				if (isPaused) {
					buttonElement.classList.add('is-paused');
				} else {
					buttonElement.classList.remove('is-paused');
				}
			}
		}
	}, [playing, isActive, play]);

	const isCurrentTrackLoaded = loadedTracks[track.id] || false;

	const wavesComponent = useMemo(() => (
		<ReactWaves
			ref={wavesurferRef}
			key={`wave-${track.id}`}
			audioFile={track.src}
			className={`react-waves react-waves-${track.id}`}
			options={{
				...options,
				barHeight,
				barWidth,
				barGap,
			}}
			volume={0}
			zoom={1}
			playing={false}
			onReady={onReady}
		/>
	), [track.id, track.src, barHeight, barWidth, barGap, onReady]);

	return (
		<div className="page__player-duration d-flex align-items-center">
			<div ref={buttonRef} onClick={toggleTrackHandler}>
				<PlayBtn
					play={playing && isActive && play}
					onClick={togglePlayPause}
					disabled={!track.src}
				/>
			</div>
			<div 
				className="player__oscillogram" 
				style={{position: 'relative', cursor: 'pointer'}}
				ref={waveContainerRef}
			>
				{!isCurrentTrackLoaded && (
					<div style={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						zIndex: 2,
						backgroundColor: 'transparent',
						display: 'flex',
						alignItems: 'center',
					}}>
						<WavePlaceholder 
							barHeight={options.barHeight}
							barWidth={options.barWidth}
							barGap={options.barGap}
						/>
					</div>
				)}
				
				<div style={{
					opacity: isCurrentTrackLoaded ? 1 : 0,
					transition: 'opacity 0.3s ease-in-out',
					visibility: isCurrentTrackLoaded ? 'visible' : 'hidden',
				}}>
					{wavesComponent}
				</div>
			</div>
			<div
				className="page__player-duration-total text--small js-page-player-song-duration-total"
			>{getTimeString(track.length)}</div>
		</div>
	);
};

export default AudioPlayer;